import React, { useState } from "react";
import { Box, Button, Chip, Collapse, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { FONT, NEUTRAL, ORANGE, RADIUS, VIOLET } from "../../styles/brandTokens";
import ApprovalRulesList from "./ApprovalRulesList";
import SettingsSection, { fieldLabelSx, optionHintSx, optionLabelSx } from "./SettingsSection";
import {
  AUTHORITY_PRESETS,
  AuthorityPreset,
  AuthorizationPolicy,
  FIXED_RULES,
  Labeled,
  applyAuthorityPreset,
  deriveAuthorityPreset,
  toggleAuthority,
} from "./authorizationPolicy";

interface AuthorizationPolicyFormProps {
  policy: AuthorizationPolicy;
  onChange: (policy: AuthorizationPolicy) => void;
  /** Rendered right after "Who can authorize", e.g. the per-customer approvers. */
  children?: React.ReactNode;
}

interface OptionCardsProps<T extends string> {
  name: string;
  legend: string;
  options: readonly Labeled<T>[];
  value: T | "";
  onChange: (value: T) => void;
  columns?: 2 | 3;
}

const controlSx = { color: NEUTRAL.borderStrong, "&.Mui-checked": { color: VIOLET.main } } as const;
const legendSx = { ...fieldLabelSx, "&.Mui-focused": { color: NEUTRAL.textSecondary } } as const;

/** Radio options laid out as bordered cards, so each choice reads as a whole policy. */
function OptionCards<T extends string>({ name, legend, options, value, onChange, columns = 2 }: OptionCardsProps<T>) {
  return (
    <FormControl component="fieldset" sx={{ width: "100%" }}>
      <FormLabel component="legend" sx={legendSx}>
        {legend}
      </FormLabel>
      <RadioGroup
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: `repeat(${columns}, minmax(0, 1fr))` } }}
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" sx={{ ...controlSx, mt: -0.25 }} />}
              label={
                <Box>
                  <Typography sx={{ ...optionLabelSx, fontWeight: 700 }}>{option.label}</Typography>
                  <Typography sx={{ ...optionHintSx, mt: 0.25 }}>{option.description}</Typography>
                </Box>
              }
              sx={{
                m: 0,
                p: 1.5,
                pr: 2,
                alignItems: "flex-start",
                borderRadius: `${RADIUS.card}px`,
                border: `1px solid ${selected ? VIOLET.main : NEUTRAL.border}`,
                background: selected ? VIOLET.wash : NEUTRAL.surface,
                transition: "border-color 150ms ease, background-color 150ms ease",
                "&:hover": { borderColor: selected ? VIOLET.main : NEUTRAL.borderStrong },
              }}
            />
          );
        })}
      </RadioGroup>
    </FormControl>
  );
}

function WhoCanAuthorize({ policy, onChange }: Pick<AuthorizationPolicyFormProps, "policy" | "onChange">) {
  const preset = deriveAuthorityPreset(policy);
  const [isCustomizing, setIsCustomizing] = useState(preset === "custom");
  const showRules = isCustomizing || preset === "custom";

  return (
    <SettingsSection
      id="authz-who"
      title="Who can authorize work on a ticket"
      description="Theo checks this once, before anyone starts the ticket, and reads the approval from the ticket, email, Slack, or Teams."
      aside={
        preset === "custom" ? (
          <Chip size="small" label="Custom" sx={{ fontFamily: FONT.sans, background: ORANGE.wash, color: ORANGE.text }} />
        ) : undefined
      }
    >
      <OptionCards<AuthorityPreset>
        name="authority-preset"
        legend="Choose a starting point"
        options={AUTHORITY_PRESETS}
        value={preset === "custom" ? "" : preset}
        onChange={(value) => onChange(applyAuthorityPreset(policy, value))}
      />
      {policy.requireApproval && (
        <>
          <Button
            onClick={() => setIsCustomizing((open) => !open)}
            disabled={preset === "custom"}
            aria-expanded={showRules}
            aria-controls="authz-rules"
            endIcon={
              <ExpandMoreOutlinedIcon sx={{ transform: showRules ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
            }
            sx={{
              mt: 2,
              fontFamily: FONT.sans,
              textTransform: "none",
              fontWeight: 600,
              color: VIOLET.main,
              borderRadius: `${RADIUS.control}px`,
            }}
          >
            Customize by request type
          </Button>
          <Collapse in={showRules} id="authz-rules">
            <Box sx={{ pt: 1.5 }}>
              <Typography sx={{ ...optionHintSx, mb: 1.5 }}>
                Select every group that may approve this kind of request. Any one of them is enough. In normal chat these checks do not
                run. In Auto Mode, Technician means an authenticated user of this MSP approving in Theo chat, as the ticket requester,
                or in a ticket note.
              </Typography>
              <ApprovalRulesList
                rules={policy.rules}
                onToggle={(requestType, authority) => onChange(toggleAuthority(policy, requestType, authority))}
              />
            </Box>
          </Collapse>
        </>
      )}
    </SettingsSection>
  );
}

export default function AuthorizationPolicyForm({ policy, onChange, children }: AuthorizationPolicyFormProps) {
  return (
    <Stack spacing={3}>
      <WhoCanAuthorize policy={policy} onChange={onChange} />
      {policy.requireApproval && (
        <>
          {children}

          <SettingsSection
            id="authz-fixed"
            title="Always enforced"
            description="These rules apply to every customer and can't be turned off."
            aside={
              <Chip size="small" icon={<LockOutlinedIcon />} label="Locked" sx={{ fontFamily: FONT.sans, color: NEUTRAL.textSecondary }} />
            }
          >
            <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 0.75 }}>
              {FIXED_RULES.map((rule) => (
                <Typography component="li" key={rule} sx={{ fontFamily: FONT.sans, fontSize: "14px", color: NEUTRAL.textSecondary }}>
                  {rule}
                </Typography>
              ))}
            </Box>
          </SettingsSection>
        </>
      )}
    </Stack>
  );
}
